import api from 'api';
import { getPublicationTimestampForElasticQuery } from 'lib/utils/dates';
import { apiPost } from 'api/typed';
import { NewspaperOrderStatus } from 'lib/types/newspaperOrder';
import {
  SORT_DESCENDING,
  SearchableNewspaperOrderRecord,
  SearchableNewspaperOrderRecordFilter,
  SearchableNoticeRecord,
  SearchableNoticeRecordFilter,
  SearchableInvoiceRecord
} from 'lib/types/searchable';
import { ColumnService } from 'lib/services/directory';

import { ERequestTypes } from 'lib/types';
import { logAndCaptureException } from 'utils';
import moment from 'moment-timezone';
import { AccountingTableRowData } from 'lib/types/reports/AccountingTableRows';
import { CustomersTableRow } from 'lib/types/reports/CustomerTableRows';
import {
  ReportsPubIssuesTableDeadline,
  ReportsPaginationReportRow
} from 'lib/types/reports/PaginationReportsRows';
import {
  ProductRevenueByMonthChartData,
  ProcessedProductsCategoryData
} from 'lib/types/reports/ProductsTable';
import { ResponseOrError, wrapSuccess, wrapError } from 'lib/types/responses';
import { SELECT_DATE_RANGES } from '../constants/dateRanges';
import { GetDateRangeFn } from '../context/ReportParamsContext';

export const getDateRangeFromSelection = (
  dateRange: string
): { startDate: string; endDate: string } => {
  const today = moment().format('YYYY-MM-DD');
  const tomorrow = moment().add(1, 'day').format('YYYY-MM-DD');
  const yesterday = moment().subtract(1, 'day').format('YYYY-MM-DD');

  switch (dateRange) {
    case SELECT_DATE_RANGES.custom.value:
      return { startDate: '', endDate: '' };
    case SELECT_DATE_RANGES.today.value:
      return {
        startDate: today,
        endDate: today
      };
    case SELECT_DATE_RANGES.tomorrow.value:
      return {
        startDate: tomorrow,
        endDate: tomorrow
      };
    case SELECT_DATE_RANGES.yesterday.value:
      return {
        startDate: yesterday,
        endDate: yesterday
      };
    case SELECT_DATE_RANGES.last7days.value:
      return {
        startDate: moment().subtract(7, 'days').format('YYYY-MM-DD'),
        endDate: today
      };
    case SELECT_DATE_RANGES.thisMonth.value:
      return {
        startDate: moment().startOf('month').format('YYYY-MM-DD'),
        endDate: moment().endOf('month').format('YYYY-MM-DD')
      };
    case SELECT_DATE_RANGES.lastMonth.value:
      return {
        startDate: moment()
          .subtract(1, 'month')
          .startOf('month')
          .format('YYYY-MM-DD'),
        endDate: moment()
          .subtract(1, 'month')
          .endOf('month')
          .format('YYYY-MM-DD')
      };
    case SELECT_DATE_RANGES.last30days.value:
    default:
      return {
        startDate: moment().subtract(30, 'days').format('YYYY-MM-DD'),
        endDate: today
      };
  }
};

export async function fetchNewspaperOrdersFromElastic(
  newspaperIds: string[],
  options: { startDate: string | undefined; endDate: string | undefined }
): Promise<SearchableNewspaperOrderRecord[]> {
  const { startDate, endDate } = options;
  if (!newspaperIds.length || !startDate || !endDate) {
    return [];
  }
  const startTimestamp = getPublicationTimestampForElasticQuery({
    dayString: startDate
  });
  const endTimestamp =
    getPublicationTimestampForElasticQuery({ dayString: endDate }) + 1;
  const filters: SearchableNewspaperOrderRecordFilter[] = [
    { publisherid: newspaperIds },
    { isactive: [1] },
    { newspaperorderstatus: [NewspaperOrderStatus.COMPLETE] },
    { lastpublishingdate: { from: startTimestamp, to: endTimestamp } }
  ];

  const pageSize = 1000;
  let pageNumber = 1;
  let allResults: SearchableNewspaperOrderRecord[] = [];
  const MAX_PAGES = 10;

  for (let i = 0; i < MAX_PAGES; i++) {
    const params: ERequestTypes['search/orders'] = {
      search: '',
      filters,
      pageSize,
      pageNumber,
      sort: [
        { createdat: SORT_DESCENDING } as Record<string, typeof SORT_DESCENDING>
      ]
    };
    try {
      // eslint-disable-next-line no-await-in-loop
      const data = await apiPost('search/orders', params);
      if (!data || data.success === false || !data.results) {
        throw new Error('Failed to fetch newspaper orders');
      }

      if (!data.results.length) {
        break;
      }

      allResults = allResults.concat(data.results);
      if (data.results.length < pageSize) {
        break;
      }

      pageNumber += 1;
    } catch (error) {
      logAndCaptureException(
        ColumnService.ELASTIC,
        error as Error,
        'Failed to fetch newspaper orders',
        { startDate, endDate }
      );
      throw error;
    }
  }

  if (pageNumber >= MAX_PAGES) {
    logAndCaptureException(
      ColumnService.ELASTIC,
      new Error(
        `Reached maximum page limit (${MAX_PAGES}) while fetching newspaper orders`
      ),
      '[Column Reports] Reached maximum page limit while fetching newspaper orders in ProductsTab'
    );
  }
  return allResults;
}

export const fetchNoticesFromElastic = async (
  orgIds: string[],
  options: { startDate: string | undefined; endDate: string | undefined }
): Promise<SearchableNoticeRecord[]> => {
  const { startDate, endDate } = options;
  if (!orgIds.length || !startDate || !endDate) {
    return [];
  }
  const startTimestamp = getPublicationTimestampForElasticQuery({
    dayString: startDate
  });
  const endTimestamp =
    getPublicationTimestampForElasticQuery({ dayString: endDate }) + 1;
  const filters: SearchableNoticeRecordFilter[] = [
    { publisherid: orgIds } as any,
    { isdraft: [0] },
    { iscancelled: [0] },
    { lastpublicationtimestamp: { from: startTimestamp, to: endTimestamp } }
  ];

  const pageSize = 1000;
  let pageNumber = 1;
  let allResults: SearchableNoticeRecord[] = [];
  const MAX_PAGES = 10;

  for (let i = 0; i < MAX_PAGES; i++) {
    const params: ERequestTypes['search/usernotices'] = {
      search: '',
      filters,
      size: pageSize,
      current: pageNumber,
      isPublisher: true,
      activeOrganizationId: undefined,
      specifiedOrganizationIds: orgIds,
      onlyExplicitFilters: true
    };
    try {
      // eslint-disable-next-line no-await-in-loop
      const data = await apiPost('search/usernotices', params);

      if (!data || data.error || !data.results) {
        throw new Error('Failed to fetch notices');
      }

      if (!data.results.length) {
        break;
      }

      allResults = allResults.concat(data.results);

      if (data.results.length < pageSize) {
        break;
      }

      pageNumber += 1;
    } catch (error) {
      logAndCaptureException(
        ColumnService.ELASTIC,
        error as Error,
        'Failed to fetch notices',
        { startDate, endDate }
      );
      throw error;
    }
  }

  if (pageNumber >= MAX_PAGES) {
    logAndCaptureException(
      ColumnService.ELASTIC,
      new Error(
        `Reached maximum page limit (${MAX_PAGES}) while fetching notices`
      ),
      '[Column Reports] Reached maximum page limit while fetching notices in NoticesTab'
    );
  }

  return allResults;
};

const getSearchParamsFromContext = (getDateRange: GetDateRangeFn) => {
  const {
    dateRange,
    startDate: customStartDate,
    endDate: customEndDate
  } = getDateRange();

  const { startDate: rangeStartDate, endDate: rangeEndDate } =
    getDateRangeFromSelection(dateRange);

  const startDate =
    dateRange === SELECT_DATE_RANGES.custom.value
      ? moment(customStartDate).format('YYYY-MM-DD')
      : rangeStartDate;

  const endDate =
    dateRange === SELECT_DATE_RANGES.custom.value
      ? moment(customEndDate).format('YYYY-MM-DD')
      : rangeEndDate;

  return { startDate, endDate };
};

export const fetchOrdersWithParams = (
  orgIds: string[],
  getDateRange: GetDateRangeFn,
  withLoading: (p: Promise<any>) => Promise<any>
): Promise<SearchableNewspaperOrderRecord[]> => {
  const { startDate, endDate } = getSearchParamsFromContext(getDateRange);

  return withLoading(
    fetchNewspaperOrdersFromElastic(orgIds, { startDate, endDate })
  );
};

export const fetchNoticesWithParams = (
  orgIds: string[],
  getDateRange: GetDateRangeFn,
  withLoading: (p: Promise<any>) => Promise<any>
): Promise<SearchableNoticeRecord[]> => {
  const { startDate, endDate } = getSearchParamsFromContext(getDateRange);

  return withLoading(fetchNoticesFromElastic(orgIds, { startDate, endDate }));
};

export const fetchProductCategoryData = async (
  orgIds: string[],
  getDateRange: GetDateRangeFn
): Promise<ProcessedProductsCategoryData[] | null> => {
  const { startDate, endDate } = getSearchParamsFromContext(getDateRange);

  if (!orgIds.length || !startDate || !endDate) {
    return null;
  }

  const startTimestamp = getPublicationTimestampForElasticQuery({
    dayString: startDate
  });

  const endTimestamp =
    getPublicationTimestampForElasticQuery({ dayString: endDate }) + 1;

  const { error, response } = await api.safePost(
    'search/product-category-aggregations',
    {
      orgIds,
      startTimestamp,
      endTimestamp
    }
  );

  if (error) {
    logAndCaptureException(
      ColumnService.ELASTIC,
      error,
      'Failed to fetch product category aggregations',
      { orgIds: orgIds.join(','), startDate, endDate }
    );
    throw error;
  }

  return response || [];
};

export const fetchCustomerNoticesAndOrdersCohorts = async (
  orgIds: string[],
  getDateRange: GetDateRangeFn
): Promise<CustomersTableRow[] | null> => {
  const { startDate, endDate } = getSearchParamsFromContext(getDateRange);

  if (!orgIds.length || !startDate || !endDate) {
    return null;
  }

  const startTimestamp = getPublicationTimestampForElasticQuery({
    dayString: startDate
  });

  const endTimestamp =
    getPublicationTimestampForElasticQuery({ dayString: endDate }) + 1;

  const { error, response } = await api.safePost(
    'search/customer-notices-and-orders-cohorts',
    {
      orgIds,
      startTimestamp,
      endTimestamp
    }
  );

  if (error) {
    throw error;
  }

  return response;
};

export const fetchProductRevenueByMonthChartData = async (
  orgIds: string[],
  getDateRange: GetDateRangeFn
): Promise<ProductRevenueByMonthChartData | null> => {
  const { startDate, endDate } = getSearchParamsFromContext(getDateRange);

  if (!orgIds.length || !startDate || !endDate) {
    return null;
  }

  const startTimestamp = getPublicationTimestampForElasticQuery({
    dayString: startDate
  });

  const endTimestamp =
    getPublicationTimestampForElasticQuery({ dayString: endDate }) + 1;

  const { error, response } = await api.safePost(
    'search/notices-and-orders-by-month-aggregations',
    {
      orgIds,
      startTimestamp,
      endTimestamp
    }
  );

  if (error) {
    throw error;
  }

  if (!response) {
    throw new Error(
      'Failed to fetch notices and orders revenue by month aggregations'
    );
  }

  return response;
};

export const fetchPublicationIssuesRowsForReports = async (
  orgIds: string[],
  getDateRange: GetDateRangeFn,
  sortByDeadline: ReportsPubIssuesTableDeadline = ReportsPubIssuesTableDeadline.AdDeadline
): Promise<ReportsPaginationReportRow[]> => {
  const { startDate, endDate } = getSearchParamsFromContext(getDateRange);

  const timezone = moment.tz.guess();

  if (!orgIds.length || !startDate || !endDate) {
    return [];
  }

  const { error, response } = await api.safePost(
    'search/pagination-table-rows',
    {
      orgIds,
      startDate,
      endDate,
      sortByDeadline,
      timezone
    }
  );

  if (error) {
    throw error;
  }

  return response || [];
};

export const fetchInvoicesByAgingRowsForReports = async (
  orgIds: string[]
): Promise<AccountingTableRowData[] | null> => {
  if (!orgIds.length) {
    return null;
  }

  const { error, response } = await api.safePost(
    'search/invoices-by-aging-aggregations',
    {
      orgIds
    }
  );

  if (error) {
    throw error;
  }

  return response;
};

export const fetchUnpaidInvoicesFromElastic = async (
  orgIds: string[],
  params: { filerId?: string; filerOrgId?: string } | undefined
): Promise<ResponseOrError<SearchableInvoiceRecord[]>> => {
  if (!orgIds.length) {
    return wrapSuccess([]);
  }

  const { error, response } = await api.safePost('search/unpaid-invoices', {
    orgIds,
    params
  });

  if (error) {
    return wrapError(new Error(error));
  }

  return wrapSuccess(response || []);
};
